import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bell, Building2, FileSignature, Globe, HardHat, Info, Menu, Plus, Receipt, Search, Truck, Users } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { type NotificationNiveau, listNotifications } from '@/services/notifications';
import { useUIStore } from '@/store/uiStore';

const CREER = [
  { label: 'Nouveau chantier', to: '/chantiers', icon: HardHat },
  { label: 'Nouvelle dépense', to: '/depenses', icon: Receipt },
  { label: 'Nouveau client', to: '/clients', icon: Building2 },
  { label: 'Nouveau fournisseur', to: '/fournisseurs', icon: Truck },
  { label: 'Nouvel employé', to: '/rh', icon: Users },
  { label: "Nouvel appel d'offres", to: '/appels-offres', icon: FileSignature },
];

const NIVEAU_UI: Record<NotificationNiveau, { icon: typeof Info; cls: string }> = {
  info: { icon: Info, cls: 'text-status-info' },
  warning: { icon: AlertTriangle, cls: 'text-status-working' },
  danger: { icon: AlertTriangle, cls: 'text-status-stuck' },
};

/**
 * Header global : recherche rapide, création rapide (menu), bascule langue,
 * et centre de notifications (alertes métier en temps réel).
 */
export function Header() {
  const lang = useUIStore((s) => s.lang);
  const setLang = useUIStore((s) => s.setLang);
  const setMobileOpen = useUIStore((s) => s.setMobileOpen);
  const navigate = useNavigate();
  const [menu, setMenu] = useState<null | 'creer' | 'notif'>(null);

  const { data: notifs = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: listNotifications,
    refetchInterval: 60_000,
  });

  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-surface-secondary px-5 print:hidden"
      style={{
        // Marge de sécurité iOS : l'en-tête ne passe jamais sous l'heure/la batterie
        // (encoche / barre d'état). env(safe-area-inset-top) vaut 0 hors mobile à encoche.
        height: 'calc(var(--header-height) + env(safe-area-inset-top))',
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      {/* Hamburger — ouvre le tiroir de navigation (mobile/tablette uniquement). */}
      <button
        onClick={() => setMobileOpen(true)}
        className="-ml-1 flex size-11 shrink-0 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover lg:hidden"
        aria-label="Ouvrir le menu de navigation"
      >
        <Menu className="size-6" />
      </button>

      <div className="relative hidden max-w-md flex-1 sm:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-muted" />
        <Input placeholder="Rechercher un chantier, marché, tiers…" className="border-transparent bg-surface-primary pl-9" aria-label="Recherche globale" />
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {/* Création rapide */}
        <div className="relative hidden sm:block">
          <Button variant="primary" size="sm" onClick={() => setMenu(menu === 'creer' ? null : 'creer')}>
            <Plus /> Nouveau
          </Button>
          {menu === 'creer' && (
            <Dropdown onClose={() => setMenu(null)} align="right">
              <p className="px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider text-content-muted">Créer</p>
              {CREER.map((c) => (
                <button
                  key={c.to}
                  onClick={() => { setMenu(null); navigate(c.to); }}
                  className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
                >
                  <c.icon className="size-4 text-brand-primary" /> {c.label}
                </button>
              ))}
            </Dropdown>
          )}
        </div>

        <button
          onClick={() => setLang(lang === 'fr' ? 'ar' : 'fr')}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-semibold uppercase text-content-secondary transition-colors hover:bg-surface-hover"
          aria-label="Changer de langue"
        >
          <Globe className="size-4" />
          {lang}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setMenu(menu === 'notif' ? null : 'notif')}
            className="relative rounded-md p-2 text-content-secondary transition-colors hover:bg-surface-hover"
            aria-label="Notifications"
          >
            <Bell className="size-5" />
            {notifs.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-status-stuck px-1 text-[10px] font-bold text-white ring-2 ring-surface-secondary">
                {notifs.length}
              </span>
            )}
          </button>
          {menu === 'notif' && (
            <Dropdown onClose={() => setMenu(null)} align="right" width="w-80">
              <div className="flex items-center justify-between px-3 pb-2 pt-1">
                <p className="text-sm font-bold text-content-primary">Notifications</p>
                <span className="text-xs text-content-muted">{notifs.length} alerte(s)</span>
              </div>
              {notifs.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-content-muted">Aucune alerte. Tout est à jour.</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {notifs.map((n) => {
                    const ui = NIVEAU_UI[n.niveau];
                    return (
                      <button
                        key={n.id}
                        onClick={() => { setMenu(null); if (n.lien) navigate(n.lien); }}
                        className="flex w-full items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
                      >
                        <ui.icon className={cn('mt-0.5 size-4 shrink-0', ui.cls)} />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-content-primary">{n.titre}</span>
                          <span className="block text-xs text-content-secondary">{n.message}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Dropdown>
          )}
        </div>
      </div>
    </header>
  );
}

function Dropdown({
  children,
  onClose,
  align = 'right',
  width = 'w-56',
}: {
  children: React.ReactNode;
  onClose: () => void;
  align?: 'left' | 'right';
  width?: string;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        className={cn(
          'absolute top-full z-50 mt-2 rounded-lg border border-border bg-surface-secondary p-1.5 shadow-lg',
          align === 'right' ? 'right-0' : 'left-0',
          width,
        )}
      >
        {children}
      </div>
    </>
  );
}
